declare const Phaser: any;

import { createTextures } from "./textures";
import { spawnAsteroid } from "./asteroids";
import { createEmitter, explode } from "./effects";

const W = 800;
const H = 400;

export function launchGame(parentId: string) {
  let ship: any;
  let cursors: any;
  let fireKey: any;
  let bullets: any;
  let asteroids: any;
  let stars: any[] = [];
  let scoreText: any;
  let livesText: any;
  let gameOverText: any;
  let restartText: any;
  let emitter: any;
  let score = 0;
  let lives = 3;
  let dead = false;
  let lastFired = 0;

  function onHitAsteroid(scene: any, bullet: any, asteroid: any) {
    bullet.setActive(false).setVisible(false);
    bullet.body.enable = false;

    const size = asteroid.getData("size");
    const ax = asteroid.x;
    const ay = asteroid.y;
    asteroid.destroy();

    if (size === "ast-lg") {
      spawnAsteroid(scene, asteroids, "ast-md", W, H, ax - 10, ay);
      spawnAsteroid(scene, asteroids, "ast-md", W, H, ax + 10, ay);
      explode(emitter, ax, ay, 0x9ca3af, 12);
      score += 20;
    } else if (size === "ast-md") {
      spawnAsteroid(scene, asteroids, "ast-sm", W, H, ax - 5, ay);
      spawnAsteroid(scene, asteroids, "ast-sm", W, H, ax + 5, ay);
      explode(emitter, ax, ay, 0xd1d5db, 8);
      score += 50;
    } else {
      explode(emitter, ax, ay, 0xffffff, 15);
      score += 100;
    }

    scoreText.setText("Score: " + score);
  }

  function onHitShip(scene: any) {
    if (dead) return;
    dead = true;
    lives--;
    livesText.setText("Lives: " + lives);

    explode(emitter, ship.x, ship.y, 0xef4444, 20);
    ship.setAlpha(0.2);

    if (lives <= 0) {
      ship.setVisible(false);
      gameOverText.setVisible(true);
      restartText.setVisible(true);
      return;
    }

    scene.time.delayedCall(1500, () => {
      ship.setPosition(W / 2, H / 2);
      ship.setVelocity(0);
      ship.setAlpha(1);
      dead = false;
    });
  }

  new Phaser.Game({
    type: Phaser.AUTO,
    parent: parentId,
    width: W,
    height: H,
    backgroundColor: "#0f0f23",
    physics: {
      default: "arcade",
      arcade: { debug: false },
    },
    scene: {
      create() {
        const scene = this;
        stars = [];
        score = 0;
        lives = 3;
        dead = false;
        lastFired = 0;

        // Background stars
        for (let i = 0; i < 100; i++) {
          stars.push(
            this.add.circle(
              Phaser.Math.Between(0, W),
              Phaser.Math.Between(0, H),
              Phaser.Math.FloatBetween(0.5, 1.5),
              0xffffff,
              Phaser.Math.FloatBetween(0.2, 0.7)
            )
          );
        }

        createTextures(this);
        emitter = createEmitter(this);

        // Ship
        ship = this.physics.add.sprite(W / 2, H / 2, "ship");
        ship.setDamping(true);
        ship.setDrag(0.97);
        ship.setMaxVelocity(250);
        ship.setCollideWorldBounds(true);

        // Groups
        bullets = this.physics.add.group({ defaultKey: "bullet", maxSize: 20 });
        asteroids = this.physics.add.group();

        // Initial asteroids
        for (let i = 0; i < 5; i++) {
          spawnAsteroid(scene, asteroids, "ast-lg", W, H);
        }

        // Spawn over time
        this.time.addEvent({
          delay: 4000,
          callback: () => {
            if (!dead && asteroids.countActive() < 15) {
              spawnAsteroid(scene, asteroids, "ast-lg", W, H);
            }
          },
          loop: true,
        });

        // Collisions
        this.physics.add.overlap(bullets, asteroids, (b: any, a: any) => {
          onHitAsteroid(scene, b, a);
        });
        this.physics.add.overlap(ship, asteroids, () => {
          onHitShip(scene);
        });

        // Controls
        cursors = this.input.keyboard.createCursorKeys();
        fireKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

        // HUD
        const hudStyle = { fontSize: "14px", fontFamily: "system-ui, sans-serif", color: "#ffffff" };
        scoreText = this.add.text(10, 10, "Score: 0", hudStyle);
        livesText = this.add.text(W - 10, 10, "Lives: 3", hudStyle).setOrigin(1, 0);

        gameOverText = this.add
          .text(W / 2, H / 2 - 20, "GAME OVER", {
            fontSize: "36px", fontFamily: "system-ui, sans-serif", fontStyle: "bold", color: "#ef4444",
          })
          .setOrigin(0.5)
          .setVisible(false);

        restartText = this.add
          .text(W / 2, H / 2 + 20, "Press SPACE to restart", {
            fontSize: "16px", fontFamily: "system-ui, sans-serif", color: "#9ca3af",
          })
          .setOrigin(0.5)
          .setVisible(false);
      },

      update(time: number) {
        for (const s of stars) {
          s.alpha = 0.3 + Math.sin(time * 0.002 + s.x) * 0.3;
        }

        if (lives <= 0) {
          if (Phaser.Input.Keyboard.JustDown(fireKey)) {
            this.scene.restart();
          }
          return;
        }

        if (dead) return;

        // Rotate
        if (cursors.left.isDown) {
          ship.setAngularVelocity(-200);
        } else if (cursors.right.isDown) {
          ship.setAngularVelocity(200);
        } else {
          ship.setAngularVelocity(0);
        }

        // Thrust
        if (cursors.up.isDown) {
          this.physics.velocityFromRotation(ship.rotation - Math.PI / 2, 300, ship.body.acceleration);
        } else {
          ship.setAcceleration(0);
        }

        // Fire
        if (Phaser.Input.Keyboard.JustDown(fireKey) && time > lastFired) {
          const bullet = bullets.get(ship.x, ship.y);
          if (bullet) {
            bullet.setActive(true).setVisible(true);
            bullet.body.enable = true;
            this.physics.velocityFromRotation(ship.rotation - Math.PI / 2, 400, bullet.body.velocity);
            lastFired = time + 150;

            this.time.delayedCall(1500, () => {
              if (bullet.active) {
                bullet.setActive(false).setVisible(false);
                bullet.body.enable = false;
              }
            });
          }
        }
      },
    },
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_HORIZONTALLY,
    },
  });
}
